import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import './index.scss'
import { ResultPage } from '@Entities/result-page'
import { OverclaimDocumentSearchResult } from '@Entities/overclaim/overclaim-document-search-result'
import { formatOverclaimDocumentStatus } from '@Entities/overclaim/overclaim-uploaded-document'
import React, { useMemo } from 'react'
import Table, { TableHeader, TableRowData } from '@Components/ViewWork/Table'
import PaginationAndLimit from '@Components/Navigations/PaginationAndLimit'
import PaginationResultsBlock from '@Components/Navigations/PaginationResultsBlock'
import CheckBox from '@Components/Selectors/Checkbox'
import { getNumTitles } from '@App/helpers/pagination/get-num-titles'
import PointPreloader from '@Components/Preloaders/PointPreloader'
import dayjs from 'dayjs'
import DocumentDownloadAction from './DocumentDownloadAction'
import LinkedOverclaimUploads from './LinkedOverclaimUploads'

interface OverclaimDocumentSelectionTableProps {
    title: string
    rightsholderId: number
    currentOverclaimHeaderId?: number
    resultsPage?: ResultPage<OverclaimDocumentSearchResult>
    paginationInfo?: PaginationInfo
    limit: number
    loading: boolean
    selectedDocumentIds: Set<number>
    showEmptyState?: boolean
    onPageChange: (pagination: PaginationBody) => void
    onSelectDocument: (document: OverclaimDocumentSearchResult) => void
    onUnselectDocument: (document: OverclaimDocumentSearchResult) => void
}

const headerRow: TableHeader = {
    row: [
        { text: 'Select/Unselect' },
        { text: 'Document Name' },
        { text: 'Document Status' },
        { text: 'Uploaded' },
        { text: 'Last Used' },
        { text: 'Linked Overclaim IDs' },
        { text: 'Actions' },
    ],
}

const OverclaimDocumentSelectionTable = ({
    title,
    rightsholderId,
    currentOverclaimHeaderId,
    resultsPage,
    paginationInfo,
    limit,
    loading,
    selectedDocumentIds,
    showEmptyState = true,
    onPageChange,
    onSelectDocument,
    onUnselectDocument,
}: OverclaimDocumentSelectionTableProps) => {
    const rows = useMemo<TableRowData[]>(
        () =>
            resultsPage?.content.map((document) => {
                const isSelected = selectedDocumentIds.has(document.memberUploadId)
                const documentId = document.memberUploadId
                const firstAttachedOverclaimUpload = document.linkedOverclaimUploads[0]
                const isLinkedToCurrentOverclaim =
                    currentOverclaimHeaderId !== undefined &&
                    document.linkedOverclaimUploads.some(
                        ({ overclaimHeaderId }) => overclaimHeaderId === currentOverclaimHeaderId
                    )

                return {
                    className: isLinkedToCurrentOverclaim ? 'already-linked-overclaim-row' : undefined,
                    removable: false,
                    cells: [
                        <CheckBox
                            key={`${documentId}-select`}
                            id={`${documentId}`}
                            mainColor
                            checked={isSelected}
                            disabled={isLinkedToCurrentOverclaim}
                            className="document-selection-checkbox"
                            onClick={(nextState) => {
                                if (nextState) {
                                    onSelectDocument(document)
                                } else {
                                    onUnselectDocument(document)
                                }
                            }}
                        />,
                        <span key={`${documentId}-name`} className="document-name-cell">
                            {document.name}
                        </span>,
                        <span key={`${documentId}-status`} className="document-status-cell">
                            {formatOverclaimDocumentStatus(document.status)}
                        </span>,
                        <span key={`${documentId}-uploaded`} className="document-uploaded-cell">
                            <div className="created-at-datetime">
                                <div>{dayjs(document.createdAt).format('MMM D, YYYY')}</div>
                                <div>{dayjs(document.createdAt).fromNow()}</div>
                            </div>
                        </span>,
                        <span key={`${documentId}-last-used`} className="document-last-used-cell">
                            {document.lastUsedAt ? dayjs(document.lastUsedAt).format('MMM D, YYYY') : '-'}
                        </span>,
                        <span key={`${documentId}-linked-overclaims`} className="linked-overclaim-uploads-cell">
                            <LinkedOverclaimUploads
                                rightsholderId={rightsholderId}
                                linkedOverclaimUploads={document.linkedOverclaimUploads}
                            />
                        </span>,
                        <DocumentDownloadAction
                            key={`${documentId}-actions`}
                            memberUploadId={document.memberUploadId}
                            overclaimHeaderId={firstAttachedOverclaimUpload.overclaimHeaderId}
                            rightsholderId={rightsholderId}
                            size={document.size}
                        />,
                    ],
                }
            }) ?? [],
        [
            currentOverclaimHeaderId,
            resultsPage,
            rightsholderId,
            selectedDocumentIds,
            onSelectDocument,
            onUnselectDocument,
        ]
    )

    if (loading) {
        return <PointPreloader />
    }

    return (
        <div className="overclaim-document-selection-table">
            <PaginationResultsBlock
                totalResults={resultsPage?.totalElements ?? 0}
                workNumTitles={getNumTitles(resultsPage?.totalElements ?? 0, paginationInfo?.currentPage ?? 1, limit)}
                resultsMessage={title}
                hideNoResults
                showCompactToggle={false}
            />
            {rows.length > 0 ? (
                <>
                    <Table headerRow={headerRow} rows={rows} />
                    <PaginationAndLimit
                        count={resultsPage?.totalElements}
                        limit={limit}
                        paginationInfo={paginationInfo}
                        onPageChange={onPageChange}
                    />
                </>
            ) : showEmptyState ? (
                <p className="empty-results-message">No documents found.</p>
            ) : null}
        </div>
    )
}

export default OverclaimDocumentSelectionTable
